// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import type {
  DefaultStandardScriptsResponse,
  DefaultStandardScript,
} from "@modules/service/reducer/default-standard-scripts/types";
import type { ApprovableContainer } from "@modules/service/types";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import {
  GetDefaultStandardScript,
  UpdateDefaultStandardScript,
  DiscardDefaultStandardScriptDraft,
  CancelDefaultStandardScriptReview,
  CancelDefaultStandardScriptReviewAndDiscard,
  SubmitDefaultStandardScriptForReview,
  StartDefaultStandardScriptDraft,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { isFirstCareUser } from "@modules/account/selectors";

type DefaultStandardScriptsRequest = $Exact<{
  clientId: string,
}>;

type ItemResponse<T> = $Exact<T>;

export type DefaultStandardScriptRequest = $Exact<{
  ...DefaultStandardScriptsRequest,
  stableId: string,
}>;

type DefaultStandardScriptUpdateRequest = $Exact<{
  ...DefaultStandardScriptRequest,
  draft: DefaultStandardScript,
}>;

export type DefaultStandardScriptSubmitForReviewRequest = $Exact<{
  ...DefaultStandardScriptRequest,
  notes: string,
}>;

export function* onGetDefaultStandardScript({
  payload,
}: Action<DefaultStandardScriptRequest>): Generator<*, void, { data: DefaultStandardScript }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}`,
    );

    yield put(actionCreators.GetDefaultStandardScriptSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDefaultStandardScriptError(e.response));
  }
}

export function* onStartDefaultStandardScriptDraft({
  payload,
}: Action<DefaultStandardScriptRequest>): Generator<*, void, { data: DefaultStandardScript }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}`,
    );

    yield all([
      put(actionCreators.StartDefaultStandardScriptDraftSuccess(response.data)),
      put(actionCreators.ToggleEditDefaultStandardScriptModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetDefaultStandardScriptError(e.response));
  }
}

export function* onUpdateDefaultStandardScript({
  payload,
}: Action<DefaultStandardScriptUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<DefaultStandardScript>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}/submit-for-review`,
        {
          notes: "Default Standard Script updated and submitted for review",
        },
      );
    }

    yield all([
      put(actionCreators.UpdateDefaultStandardScriptSuccess(response.data)),
      put(actionCreators.ToggleEditDefaultStandardScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", { clientId: payload.clientId }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateDefaultStandardScriptError(e.response));
  }
}

export type DefaultStandardScriptDisableRequest = $Exact<{
  ...DefaultStandardScriptRequest,
  approvable: ApprovableContainer<DefaultStandardScript>,
}>;

export function* onDiscardDefaultStandardScriptDraft({
  payload,
}: Action<DefaultStandardScriptRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.DiscardDefaultStandardScriptDraftSuccess()),
      put(actionCreators.SetShowDiscardDefaultStandardScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", { clientId: payload.clientId }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_DISCARDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardDynamicQuestionDraftError(e.response));
  }
}

export function* onCancelDefaultStandardScriptReview({
  payload,
}: Action<DefaultStandardScriptRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}/cancel-review`,
    );

    yield all([
      put(actionCreators.CancelDefaultStandardScriptReviewSuccess()),
      put(actionCreators.SetShowCancelDefaultStandardScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", { clientId: payload.clientId }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_CANCELED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelDefaultStandardScriptReviewError(e.response));
  }
}

export function* onCancelDefaultStandardScriptReviewAndDiscard({
  payload,
}: Action<DefaultStandardScriptRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.CancelDefaultStandardScriptReviewAndDiscardSuccess()),
      put(actionCreators.SetShowCancelDiscardDefaultStandardScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", { clientId: payload.clientId }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_CANCELED_AND_DISCARDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelDynamicQuestionReviewAndDiscardError(e.response));
  }
}

export function* onSubmitDefaultStandardScriptForReview({
  payload,
}: Action<DefaultStandardScriptSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield all([
      put(actionCreators.SubmitDefaultStandardScriptForReviewSuccess()),
      put(actionCreators.SetShowSubmitDefaultStandardScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", { clientId: payload.clientId }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_SUBMITTED")),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitDefaultStandardScriptForReviewError(e.response));
  }
}

export function* defaultStandardScriptsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetDefaultStandardScript, onGetDefaultStandardScript),
    takeEvery(StartDefaultStandardScriptDraft, onStartDefaultStandardScriptDraft),
    takeEvery(UpdateDefaultStandardScript, onUpdateDefaultStandardScript),
    takeEvery(DiscardDefaultStandardScriptDraft, onDiscardDefaultStandardScriptDraft),
    takeEvery(CancelDefaultStandardScriptReview, onCancelDefaultStandardScriptReview),
    takeEvery(
      CancelDefaultStandardScriptReviewAndDiscard,
      onCancelDefaultStandardScriptReviewAndDiscard,
    ),
    takeEvery(SubmitDefaultStandardScriptForReview, onSubmitDefaultStandardScriptForReview),
  ]);
}

export default defaultStandardScriptsSagas;
